import { dateFns, dateTimeFns, durationFns, instantFns, monthDayFns, timeFns, yearMonthFns, zonedDateTimeFns } from 'iso-fns2';
import { addIssueToContext, z } from 'zod';
class ZodCustomType extends z.ZodType {
    _parse(input) {
        const { data } = input;
        const ctx = this._getOrReturnCtx(input);
        if (!this._def.typeGuard(data)) {
            addIssueToContext(ctx, {
                code: z.ZodIssueCode.invalid_type,
                expected: this._def.typeName,
                received: ctx.parsedType
            });
            return z.INVALID;
        }
        else {
            return z.OK(data);
        }
    }
    get label() {
        return this._def.label || null;
    }
    get permissions() {
        return this._def.permissions || [];
    }
}
export const ZodCustomTypeKind = {
    ZodInstant: 'isoInstant',
    ZodDateTime: 'isoDateTime',
    ZodZonedDateTime: 'isoZonedDateTime',
    ZodDate: 'isoDate',
    ZodTime: 'isoTime',
    ZodYearMonth: 'isoYearMonth',
    ZodMonthDay: 'isoMonthDay',
    ZodDuration: 'isoDuration'
};
export class ZodIsoInstant extends ZodCustomType {
    setLabel(label) {
        return new ZodIsoInstant({
            ...this._def,
            label
        });
    }
    setPermissions(...permissions) {
        const merged = permissions.reduce((acc, cur) => [...acc, ...cur], []);
        return new ZodIsoInstant({
            ...this._def,
            permissions: merged
        });
    }
    init() {
        return this._def.initFn ? this._def.initFn() : instantFns.now();
    }
    initFn(init) {
        return new ZodIsoInstant({
            ...this._def,
            initFn: init
        });
    }
}
export class ZodIsoDateTime extends ZodCustomType {
    setLabel(label) {
        return new ZodIsoDateTime({
            ...this._def,
            label
        });
    }
    setPermissions(...permissions) {
        const merged = permissions.reduce((acc, cur) => [...acc, ...cur], []);
        return new ZodIsoDateTime({
            ...this._def,
            permissions: merged
        });
    }
    init() {
        return this._def.initFn ? this._def.initFn() : dateTimeFns.now();
    }
    initFn(init) {
        return new ZodIsoDateTime({
            ...this._def,
            initFn: init
        });
    }
}
export class ZodIsoZonedDateTime extends ZodCustomType {
    setLabel(label) {
        return new ZodIsoZonedDateTime({
            ...this._def,
            label
        });
    }
    setPermissions(...permissions) {
        const merged = permissions.reduce((acc, cur) => [...acc, ...cur], []);
        return new ZodIsoZonedDateTime({
            ...this._def,
            permissions: merged
        });
    }
    init() {
        return this._def.initFn ? this._def.initFn() : zonedDateTimeFns.now();
    }
    initFn(init) {
        return new ZodIsoZonedDateTime({
            ...this._def,
            initFn: init
        });
    }
}
export class ZodIsoDate extends ZodCustomType {
    setLabel(label) {
        return new ZodIsoDate({
            ...this._def,
            label
        });
    }
    setPermissions(...permissions) {
        const merged = permissions.reduce((acc, cur) => [...acc, ...cur], []);
        return new ZodIsoDate({
            ...this._def,
            permissions: merged
        });
    }
    init() {
        return this._def.initFn ? this._def.initFn() : dateFns.now();
    }
    initFn(init) {
        return new ZodIsoDate({
            ...this._def,
            initFn: init
        });
    }
}
export class ZodIsoTime extends ZodCustomType {
    setLabel(label) {
        return new ZodIsoTime({
            ...this._def,
            label
        });
    }
    setPermissions(...permissions) {
        const merged = permissions.reduce((acc, cur) => [...acc, ...cur], []);
        return new ZodIsoTime({
            ...this._def,
            permissions: merged
        });
    }
    init() {
        return this._def.initFn ? this._def.initFn() : timeFns.now();
    }
    initFn(init) {
        return new ZodIsoTime({
            ...this._def,
            initFn: init
        });
    }
}
export class ZodIsoYearMonth extends ZodCustomType {
    setLabel(label) {
        return new ZodIsoYearMonth({
            ...this._def,
            label
        });
    }
    setPermissions(...permissions) {
        const merged = permissions.reduce((acc, cur) => [...acc, ...cur], []);
        return new ZodIsoYearMonth({
            ...this._def,
            permissions: merged
        });
    }
    init() {
        return this._def.initFn ? this._def.initFn() : dateFns.toYearMonth(dateFns.now());
    }
    initFn(init) {
        return new ZodIsoYearMonth({
            ...this._def,
            initFn: init
        });
    }
}
export class ZodIsoMonthDay extends ZodCustomType {
    setLabel(label) {
        return new ZodIsoMonthDay({
            ...this._def,
            label
        });
    }
    setPermissions(...permissions) {
        const merged = permissions.reduce((acc, cur) => [...acc, ...cur], []);
        return new ZodIsoMonthDay({
            ...this._def,
            permissions: merged
        });
    }
    init() {
        return this._def.initFn ? this._def.initFn() : dateFns.toMonthDay(dateFns.now());
    }
    initFn(init) {
        return new ZodIsoMonthDay({
            ...this._def,
            initFn: init
        });
    }
}
export class ZodIsoDuration extends ZodCustomType {
    setLabel(label) {
        return new ZodIsoDuration({
            ...this._def,
            label
        });
    }
    setPermissions(...permissions) {
        const merged = permissions.reduce((acc, cur) => [...acc, ...cur], []);
        return new ZodIsoDuration({
            ...this._def,
            permissions: merged
        });
    }
    init() {
        return this._def.initFn ? this._def.initFn() : durationFns.fromNumbers(0);
    }
    initFn(init) {
        return new ZodIsoDuration({
            ...this._def,
            initFn: init
        });
    }
}
export const isoZ = {
    ZodIsoInstant: ZodIsoInstant,
    ZodIsoDateTime: ZodIsoDateTime,
    ZodIsoDate: ZodIsoDate,
    ZodIsoTime: ZodIsoTime,
    ZodIsoYearMonth: ZodIsoYearMonth,
    ZodIsoMonthDay: ZodIsoMonthDay,
    ZodIsoDuration: ZodIsoDuration,
    instant() {
        return new ZodIsoInstant({ typeName: ZodCustomTypeKind.ZodInstant, typeGuard: instantFns.isValid });
    },
    dateTime() {
        return new ZodIsoDateTime({ typeName: ZodCustomTypeKind.ZodDateTime, typeGuard: dateTimeFns.isValid });
    },
    zonedDateTime() {
        return new ZodIsoZonedDateTime({ typeName: ZodCustomTypeKind.ZodZonedDateTime, typeGuard: zonedDateTimeFns.isValid });
    },
    date(params) {
        return new ZodIsoDate({ typeName: ZodCustomTypeKind.ZodDate, typeGuard: dateFns.isValid, errorMap: params?.errorMap });
    },
    time() {
        return new ZodIsoTime({ typeName: ZodCustomTypeKind.ZodTime, typeGuard: timeFns.isValid });
    },
    yearMonth() {
        return new ZodIsoYearMonth({ typeName: ZodCustomTypeKind.ZodYearMonth, typeGuard: yearMonthFns.isValid });
    },
    monthDay() {
        return new ZodIsoMonthDay({ typeName: ZodCustomTypeKind.ZodMonthDay, typeGuard: monthDayFns.isValid });
    },
    duration() {
        return new ZodIsoDuration({ typeName: ZodCustomTypeKind.ZodDuration, typeGuard: durationFns.isValid });
    }
};
